import { useState, useCallback, useEffect, useRef } from 'react';
import type { ChatMessage } from '../types/foreman';
import type { EventFrame, ChatEventPayload, ChatMessageContent, ContentPart, UserRole } from '../types/gateway';
import { parseForemanResponse } from '../gateway/parser';
import { buildChatSend, fileToAttachment } from '../gateway/protocol';
import { gateway } from '../gateway/connection';
import { isExtractableDocument, extractDocumentText } from '../extractors';
import { buildAgentRoleContext } from '../config/roles';

interface UseMessagesProps {
  activeSessionId: string | null;
  teachMode?: boolean;
  chatHistory?: ChatMessage[];
  loadingHistory?: boolean;
  /** Active UI role — sent to the agent on every chat.send so it can adapt. */
  role?: UserRole;
}

/** Extract plain text from an OpenClaw message content */
function extractText(msg: ChatMessageContent | undefined): string {
  if (!msg) return '';
  if (typeof msg.content === 'string') return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content
      .filter((p: ContentPart) => p.type === 'text')
      .map((p) => (p as { text: string }).text)
      .join('');
  }
  return '';
}

export function useMessages({ activeSessionId, teachMode = false, chatHistory = [], loadingHistory = false, role }: UseMessagesProps) {
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamRef = useRef<string>('');
  const runIdRef = useRef<string | null>(null);
  const prevSessionRef = useRef<string | null>(null);

  // Clear live messages when session changes
  useEffect(() => {
    if (prevSessionRef.current !== activeSessionId) {
      prevSessionRef.current = activeSessionId;
      setLiveMessages([]);
      streamRef.current = '';
      setIsStreaming(false);
      runIdRef.current = null;
    }
  }, [activeSessionId]);

  // Merge: history (from useSessions) + live messages (from this session's events)
  const messages = [...chatHistory, ...liveMessages];

  useEffect(() => {
    // Listen for chat events from the gateway
    const unsub = gateway.onEvent((evt: EventFrame) => {
      if (evt.event !== 'chat') return;

      const payload = evt.payload as ChatEventPayload;
      if (!payload) return;

      const { state, message, runId } = payload;
      const rawSessionKey = payload.sessionKey || 'main';

      // Event keys come as "agent:main:KEY" — extract the short key to match activeSessionId
      const sessionKey = rawSessionKey.includes(':')
        ? rawSessionKey.split(':').slice(2).join(':') || rawSessionKey
        : rawSessionKey;

      // Only handle events for the active session
      if (sessionKey !== activeSessionId) return;

      const msgId = runId || self.crypto?.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36));

      if (state === 'delta') {
        setIsStreaming(true);
        runIdRef.current = runId || null;
        const text = extractText(message);
        if (typeof text === 'string' && text.length > 0) {
          // OpenClaw sends accumulated text, not incremental deltas
          if (text.length >= (streamRef.current?.length || 0)) {
            streamRef.current = text;
          }
        }

        const parsed = parseForemanResponse(streamRef.current);
        setLiveMessages((prev) => {
          const existing = prev.find((m) => m.id === msgId);
          if (existing) {
            return prev.map((m) =>
              m.id === msgId ? { ...m, content: streamRef.current, parsed } : m
            );
          }
          return [
            ...prev,
            {
              id: msgId,
              type: 'foreman',
              content: streamRef.current,
              parsed,
              sender: 'Foreman',
              timestamp: new Date().toISOString(),
              sessionId: sessionKey,
            },
          ];
        });
      } else if (state === 'final') {
        setIsStreaming(false);
        const text = extractText(message) || streamRef.current;
        streamRef.current = '';
        runIdRef.current = null;

        if (text.trim()) {
          const parsed = parseForemanResponse(text);
          setLiveMessages((prev) => {
            const existing = prev.find((m) => m.id === msgId);
            if (existing) {
              return prev.map((m) =>
                m.id === msgId ? { ...m, content: text, parsed } : m
              );
            }
            return [
              ...prev,
              {
                id: msgId,
                type: 'foreman',
                content: text,
                parsed,
                sender: 'Foreman',
                timestamp: new Date().toISOString(),
                sessionId: sessionKey,
              },
            ];
          });
        }
      } else if (state === 'aborted') {
        setIsStreaming(false);
        streamRef.current = '';
        runIdRef.current = null;
      }
    });

    return unsub;
  }, [activeSessionId]);

  const sendMessage = useCallback(
    async (content: string, files?: File[]) => {
      if (!content.trim() && (!files || files.length === 0)) return;

      let gatewayContent = teachMode ? `[TEACH] ${content}` : content;

      // Add user message to local state
      const userMsg: ChatMessage = {
        id: self.crypto?.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36)),
        type: 'user',
        content,
        sender: 'You',
        timestamp: new Date().toISOString(),
        sessionId: activeSessionId || 'main',
      };
      setLiveMessages((prev) => [...prev, userMsg]);

      // Separate files: images go as attachments, documents get text extracted
      const imageFiles: File[] = [];
      const docFiles: File[] = [];
      if (files) {
        for (const f of files) {
          if (isExtractableDocument(f)) {
            docFiles.push(f);
          } else {
            imageFiles.push(f);
          }
        }
      }

      // Extract text from documents client-side
      if (docFiles.length > 0) {
        const results = await Promise.all(docFiles.map(extractDocumentText));
        const blocks: string[] = [];
        for (const result of results) {
          if (result.ok) {
            const ext = result.fileName.split('.').pop()?.toUpperCase() || 'FILE';
            blocks.push(`[Extracted from ${result.fileName} (${ext})]:\n\n${result.text}`);
          } else {
            blocks.push(`[Failed to extract ${result.fileName}: ${result.error}. Please paste the text directly or send a photo/screenshot.]`);
          }
        }
        if (blocks.length > 0) {
          gatewayContent = gatewayContent
            ? gatewayContent + '\n\n' + blocks.join('\n\n')
            : blocks.join('\n\n');
        }
      }

      // Build attachments from image files only (OpenClaw format)
      const attachments = imageFiles.length > 0
        ? await Promise.all(imageFiles.map(fileToAttachment))
        : undefined;

      // Send via gateway request — attach role context so the agent adapts
      // its behavior to the user's selected role.
      const sessionKey = activeSessionId || 'main';
      const roleContext = role ? buildAgentRoleContext(role) : undefined;
      const params = buildChatSend(sessionKey, gatewayContent, attachments, roleContext);

      // Reset stream state for incoming response
      streamRef.current = '';
      setIsStreaming(true);

      try {
        await gateway.request('chat.send', params);
      } catch (err) {
        console.error('[useMessages] chat.send failed:', err);
        setIsStreaming(false);
      }
    },
    [activeSessionId, teachMode, role]
  );

  const clearMessages = useCallback(() => {
    setLiveMessages([]);
    streamRef.current = '';
    setIsStreaming(false);
  }, []);

  return {
    messages,
    isStreaming: isStreaming || loadingHistory,
    sendMessage,
    clearMessages,
  };
}
